import { CommonModule } from "@angular/common";
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Observable } from "rxjs";
import { TranslateModule } from "@ngx-translate/core";

import { createLeadRequestObject } from "@zp-core/mappers/aanvraag-to-lead.mapper";
import { Question } from "@zp-models/question";
import { ApiService } from "@zp-services/api.service";
import { StateService } from "@zp-services/state.service";
import { NesFormControlDirective } from "@zp-utils/directives/nes-form-control.directive";

@Component({
  standalone: true,
  selector: "app-aanvragen-step",
  templateUrl: "./aanvragen-step.component.html",
  styleUrls: ["./aanvragen-step.component.css"],
  imports: [TranslateModule, CommonModule, ReactiveFormsModule, NesFormControlDirective],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AanvragenStepComponent implements OnInit {
  form: FormGroup;

  question!: Question;
  questionId!: string;

  noOfSteps!: number;
  currentStepNumber!: number;
  stepsConfig!: Record<string, Question>;

  getTranslatedOptionsArray: Record<string, Observable<{ value: string; label: string }[]>> = {};

  constructor(
    private stateService: StateService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit() {
    this.stepsConfig = this.stateService.getAanvragenStepsConfig();

    this.route.data.subscribe((data) => {
      this.questionId = data["stepId"]; // Deze data is nog steeds nodig en correct.
      this.question = this.stepsConfig[this.questionId];

      this.currentStepNumber = Object.keys(this.stepsConfig).indexOf(this.questionId) + 1;

      const savedData = this.stateService.getAnswer(this.questionId);
      this.buildForm(savedData);
    });
  }

  buildForm(savedData?: any): void {
    const group: Record<string, any> = {};

    this.question.fields.forEach((field) => {
      const validators = [];
      if (field.required) {
        validators.push(Validators.required);
      }

      if (field.type === "checkbox") {
        group[field.name] = this.fb.array([], validators);
      } else {
        group[field.name] = new FormControl("", validators);
      }
    });

    this.form = this.fb.group(group);

    if (savedData) {
      this.patchForm(savedData);
    }
  }

  patchForm(savedData: any): void {
    Object.keys(savedData).forEach((key) => {
      const control = this.form.get(key);

      if (control instanceof FormArray && Array.isArray(savedData[key])) {
        control.clear();
        savedData[key].forEach((val: any) => {
          (control as FormArray).push(new FormControl(val));
        });
      } else if (control) {
        control.setValue(savedData[key]);
      }
    });
  }
  onSelectableRadioChange(event: CustomEvent): void {
    const selectedValue = event.detail?.value;

    if (!selectedValue) return;

    const matchingField = this.question.fields.find(
      (field) => field.type === "radio" && field.options?.some((opt) => opt.value === selectedValue)
    );

    if (matchingField) {
      this.form.get(matchingField.name)?.setValue(selectedValue);
      this.form.get(matchingField.name)?.markAsTouched();
    } else {
      console.warn("[SelectableChange] No matching field found for value:", selectedValue);
    }
  }

  onSubmit() {
    this.form.markAllAsTouched();
    this.stateService.setStepData(this.questionId, this.form.value);

    const wizardAnswers = this.stateService.getAnswers();
    const payload = createLeadRequestObject(wizardAnswers);
    console.log(payload);

    if (this.form.invalid) {
      console.warn("[Form Submission] Form is invalid. Please fill in all required fields.");
      return;
    }

    const answers = this.stateService.getAnswers();

    const lead = createLeadRequestObject(answers);
    console.log(lead);

    this.stateService.setStepData(this.questionId, this.form.value);
    this.apiService.createLead(lead).subscribe({
      next: (response) => {
        const targetUrl = "/aanvragen/checkout";
        this.router.navigate([targetUrl]);
      },
      error: (error) => {
        console.error("Error creating lead:", error);
        alert(
          "Er is een fout opgetreden bij het aanmaken van uw aanvraag. Probeer het later opnieuw."
        );
      },
    });
  }

  hasError(fieldName: string, errorType: string): boolean | null {
    const control = this.form.get(fieldName);
    return control && control.hasError(errorType) && (control.dirty || control.touched);
  }
}
